<template>
  <!-- basic modal -->
  <b-row>
    <b-col
      class="problem-card-title text-center"
      md="12"
    >
      <h4 class="mb-0">
        {{ getProblemHintsDescription($t('student-test-module.speech-problem-hint')) }}
      </h4>
    </b-col>

    <!-- Problem Conversion Mode Toggle - Only show if speech recognition available AND allowed -->
    <b-col
      v-if="isProblemConversionAllowed && hasSpeechRecognition"
      md="12"
      class="text-center mb-2"
    >
      <b-form-group
        class="mb-0"
        :label="$t('student-test-module.answer-mode') || 'Answer Mode'"
        label-class="font-weight-bold"
      >
        <b-form-radio-group
          v-model="problemConversion"
          :options="problemConversionOptions"
          name="problem-conversion-mode"
          buttons
          button-variant="outline-primary"
          :disabled="isAnswerSubmitted || displayResult"
        />
      </b-form-group>
    </b-col>

    <b-col
      md="12"
      class="mtf-main-content problem-overview-content m-0  p-0"
    >
      <b-row
        v-for="(question, i) in localQuestions"
        :key="i"
        class="m-0 p-0"
      >
        <b-col v-if="activeIndex === i">
          <div class="question m-lg-4 m-md-2 border pl-1 pt-1 border-speech">
            <label class="h2 mb-1">{{ $t('labels.speech-question') }}</label><br>
            <div class=" rounded p-1 row">
              <b-col md="9">
                <AudioRecorder
                  :src="tts(i, 'questions')"
                  :only-icon="true"
                  :include-graph="true"
                  class="problem-audio"
                >
                  {{ question.text }}
                </AudioRecorder>
              </b-col>
              <b-col md="3">
                <b-img
                  v-if="question.imageUrl || question.file"
                  :src="question.imageUrl ? question.imageUrl : question.file"
                  style="height:150px; width:190px;object-fit: contain;"
                  class="img-fluid"
                />
              </b-col>
            </div>
          </div>
          <ProblemWithConservation
            v-if="effectiveMode"
            :question="question"
            @updateAnswer="({ answer, ansObject }) => {
              ansObject.recordedAnswer = {
                text: answer,
              };
              $forceUpdate()
            }"
          />
          <div v-else>
            <div class="answer mx-lg-4 mt-lg-4 mx-md-2 mt-md-2">
              <SpeechArea
                v-for="ans,ai of question.answers"
                :key="ai"
                :answer="ans"
                :lang="rules?.lang"
                :tries-length="triesLength"
                :is-answer-submitted="isAnswerSubmitted"
                :show-preview-answer="showPreviewAnswer"
                :preview-answers="getPreviewAnswer"
                @updateAnswer="a => { ans.recordedAnswer = a; $forceUpdate() }"
              />
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row class="mx-4 mt-2 d-flex justify-content-end">
        <b-button
          v-if="localQuestions.length != activeIndex + 1"
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="primary"
          :disabled="localQuestions[activeIndex].answers.some(it => !it.recordedAnswer)"
          @click="activeIndex += 1"
        >
          {{ $t("actions.next") }}
        </b-button>
      </b-row>
    </b-col>
    <portal to="problemFeedBack">
      <b-row>
        <b-col
          md="12"
          class="p-0"
        >
          <feedback
            :feedback="feedback"
            :problem-statement-id="problemStatementId"
            :speech-accuracy-test-stat-id="feedbackTestIdForSpeech"
            :is-demo="!!feedback.is_demo"
          />
        </b-col>
        <b-col
          v-if="!isPreview"
          class="m-0 p-0"
          md="12"
        >
          <actions
            :is-processing="isProcessing"
            :on-submit="submit"
            :level="level ? level : ''"
            :disabled="!(activeIndex === localQuestions.length - 1) || localQuestions[activeIndex].answers.some(it => !it.recordedAnswer)"
            :has-minimal-view="hasMinimalView"
            :has-feedback="hasFeedback"
            :tooltip-text="$t('student-test-module.match-problem-validation-message')"
            :display-result="displayResult"
            :button-title="hasFeedback ? $t('next') : $t('submit')"
            :engine="engine"
            :school-settings="schoolSettings"
            :district-settings="districtSettings"
            v-on="$listeners"
          />
        </b-col>
      </b-row>
    </portal>
  </b-row>
</template>

<script>
import {
  BRow, BCol, BButton, BImg, BFormGroup, BFormRadioGroup,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import AudioRecorder from '@/views/common/components/AudioPlayer.vue'
import Actions from '../Actions.vue'
import Feedback from '../Feedback.vue'
import SpeechArea from './speech-area.vue'
import ProblemWithConservation from './ProblemWithConservation.vue'

export default ({
  components: {
    BRow,
    BCol,
    Actions,
    Feedback,
    BButton,
    BImg,
    BFormGroup,
    BFormRadioGroup,
    AudioRecorder,
    SpeechArea,
    ProblemWithConservation,
  },
  directives: {
    Ripple,
  },
  props: {
    questions: { type: Array, required: true },
    previewAnswers: { type: Object, default: () => {} },
    isProcessing: { type: Boolean, default: false },
    feedback: { type: Object, default: () => {} },
    displayResult: { type: Boolean, default: false },
    isPreview: { type: Boolean, default: false },
    mode: { type: String, required: true },
    level: { type: Number, default: 1 },
    problemStatementId: { type: [Number, String], default: () => '' },
    startTime: { type: [String, Date], required: false, default: () => null },
    engine: { type: String, default: () => '' },
    hasMinimalView: { type: Boolean, default: false },
    testId: { type: Number, default: 0 },
    schoolSettings: { type: Array, default: () => [] },
    districtSettings: { type: Array, default: () => [] },
    rules: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      localQuestions: [],
      localAnswers: [],
      questionAnswer: {},
      selectedAnswer: null,
      questionDataType: 'text',
      answerDataType: 'text',
      voices: [],
      selectedVoice: null,
      synth: null,
      activeIndex: 0,
      activeAnswer: 0,
      isAnswerSubmitted: false,
      // Local state replacing the Vuex-backed computed
      problemConversion: false,
      // Flag to track speech recognition API availability
      hasSpeechRecognition: false,
    }
  },
  computed: {
    getPreviewAnswer() {
      if (this.previewAnswers?.combined_ans) {
        return this.previewAnswers?.combined_ans
      }
      return this.previewAnswers
    },
    activeQuestion() {
      return this.localQuestions[this.activeIndex]
    },
    hasFeedback() {
      return !!(this.feedback?.text?.length > 0 || this.feedback?.image || this.feedback?.audio || this.feedback?.template)
    },
    feedbackTestIdForSpeech() {
      if (this.feedback.test_id && this.feedback.hasSpeechCheck) {
        return this.feedback.test_id
      }
      if (this.isPreview && this.testId) {
        return this.testId
      }
      return null
    },
    isMatch() {
      const question = this.localQuestions[this.activeIndex]
      const answer = question.answers[0].text.replace(/[,.!\s]/gi, '').toLowerCase()
      return question.recordedAnswers.map(item => item.text).includes(answer)
    },
    showPreviewAnswer() {
      if (this.previewAnswers && Object.keys(this.previewAnswers).length > 0) return true
      return false
    },
    classId() {
      return this.$route.params.classId
    },
    triesLength() {
      return this.$store.state.session.settings.speechTries || 3
    },
    isProblemConversionAllowed() {
      return this.$store.state.problem.isProblemConversionAllowed
    },
    problemConversionOptions() {
      return [
        { text: this.$t('student-test-module.speech-mode') || 'Speech', value: false },
        { text: this.$t('student-test-module.text-mode') || 'Text', value: true },
      ]
    },
    // Effective mode: forced to true (text/conversation) if no speech recognition available
    effectiveMode() {
      if (!this.hasSpeechRecognition) {
        return true
      }
      return this.problemConversion
    },
  },
  watch: {
    questions() {
      this.initData()
    },
    problemConversion() {
      // Reset recorded answers when switching modes to avoid stale state
      this.resetRecordedAnswers()
    },
  },
  created() {
    this.checkSpeechRecognitionAvailability()
    this.initData()

    // If no speech recognition, force conversation mode
    if (!this.hasSpeechRecognition) {
      this.problemConversion = true
    } else {
      // Initialize local toggle from store (if present) so we keep backward compatibility
      const storeValue = this.$store?.state?.problem?.problemConversion
      if (typeof storeValue === 'boolean') {
        this.problemConversion = storeValue
      }
    }

    this.synth = window.speechSynthesis
    setTimeout(() => {
      this.voices = [...this.synth.getVoices().map((v, i) => ({
        text: v.name, value: i, voice: v, lang: v.lang,
      }))]
      this.selectedVoice = this.voices.find(v => (v.lang === 'en-US'))
      const [first] = this.voices
      if (!this.selectedVoice) this.selectedVoice = first
    }, 1000)
    if (this.previewAnswers) {
      const previewAnswerKeys = Object.keys(this.previewAnswers)
      if (previewAnswerKeys && previewAnswerKeys.length) {
        previewAnswerKeys.forEach(keys => {
          const answers = this.localQuestions.find(ques => `${ques.id}` === keys)
          if (answers) {
            answers.answers[0].audio = this.previewAnswers[keys].audio
          }
        })
      }
    }
  },
  methods: {
    /**
     * Check if browser supports Web Speech API (webkitSpeechRecognition)
     */
    checkSpeechRecognitionAvailability() {
      this.hasSpeechRecognition = !!(
        window.SpeechRecognition
        || window.webkitSpeechRecognition
        || window.mozSpeechRecognition
        || window.msSpeechRecognition
      )
    },
    initData() {
      this.localQuestions = JSON.parse(JSON.stringify(this.questions)).map(q => ({
        ...q, recordedAnswer: {}, recordedAnswers: [], audio: '',
      }))
      this.localAnswers = this.questions.map(e => e.answers[0])
      this.questionDataType = this.questions[0].data_type
      this.answerDataType = this.localAnswers[0].data_type
    },
    resetRecordedAnswers() {
      this.localQuestions.forEach(q => {
        // eslint-disable-next-line no-param-reassign
        q.recordedAnswer = {}
        // eslint-disable-next-line no-param-reassign
        q.recordedAnswers = []
        if (Array.isArray(q.answers)) {
          q.answers.forEach(a => {
            // eslint-disable-next-line no-param-reassign
            a.recordedAnswer = undefined
          })
        }
      })
      this.$forceUpdate()
    },
    async convertBlobUrlToFile(blobUrl, fileName) {
      try {
        const response = await fetch(blobUrl)
        if (!response.ok) {
          throw new Error(`Failed to fetch Blob URL: ${response.status} ${response.statusText}`)
        }
        const blob = await response.blob()
        const file = new File([blob], fileName, { type: 'audio/wav; codecs=audio/pcm; samplerate=16000' })
        return file
      } catch (error) {
        throw new Error(`Error converting Blob URL to File: ${error.message}`)
      }
    },
    async submit() {
      const finalAnswer = []
      for (let i = 0; i < this.localQuestions.length; i += 1) {
        const question = this.localQuestions[i]
        for (let j = 0; j < question.answers.length; j += 1) {
          const answer = question.answers[j].recordedAnswer
          let audio = '-' // For conservative mode fix
          if (!this.effectiveMode) {
            // eslint-disable-next-line no-await-in-loop
            audio = await this.convertBlobUrlToFile(answer?.audio, 'recorded.wav')
          }
          finalAnswer.push({
            id: `${question.id}-${question.answers[j].id}`,
            audio,
            text: answer?.text,
          })
        }
        this.isAnswerSubmitted = true
        this.$emit('submitQuestion', {
          lang_level: this.localQuestions[0].lang_level,
          answer: finalAnswer,
          // Optional: surface the chosen mode to the parent for analytics/handling
          problem_conversion: this.effectiveMode,
        })
      }
    },
    // eslint-disable-next-line consistent-return
    tts(j, type) {
      let audioFile
      if (type === 'questions') {
        const objectInfo = this.localQuestions[j]
        if (objectInfo.extra_file) {
          audioFile = objectInfo.extra_file
        } else {
          audioFile = objectInfo.text
        }
      } else {
        const objectInfo = this.localQuestions[j].answers[0]
        if (objectInfo.extra_file) {
          audioFile = objectInfo.extra_file
        } else {
          audioFile = objectInfo.text
        }
      }
      return audioFile
    },
  },
})
</script>

<style lang="scss">

  .selected-answer{
    box-sizing: border-box !important;
    border: 5px #f1c40f solid !important;
  }
  .drop-zone{
    border:1px solid grey;
    padding:4px;
    box-sizing: border-box;
  }
  .drop-zone-image{
    height:150px;
  }
  .drop-zone-audio{
    height:90px;
  }
  .drop-zone-audio audio{
    max-width: 180px;
  }

  .draggable-type-audio audio{
    max-width: 180px;
  }
  .media audio{
    width: 100%;
  }

  .match-question-item{
    border: 0px solid white;
  }
  .match-question-item:nth-child(odd){
    border-right-width: 1px;
    border-bottom-width: 1px;
  }
  .match-question-item:nth-child(even){
    border-bottom-width: 1px;
  }
  .play-icon {
    width: 40px;
    height: 40px;
    &:hover{
      cursor: pointer;
    }
  }

  .speech-answers{
    padding:20px;
  }
  .theme-transparent .border-speech {
    border-color: black !important;
  }
  .problem-audio {
    span {
      display: flex;
    }
  }
  @media (max-width: 576px) {
  .problem-card-title h4{
    font-size: 20px !important;
    font-weight: 500 !important;
  }
}
</style>
